import { supabase } from './supabase';

interface CachedToken {
  token: string;
  expiresAt: number;
}

class TokenManager {
  private static BUFFER_TIME = 5 * 60 * 1000; // 5 minutes before expiry
  private cachedToken: CachedToken | null = null;

  private isTokenExpired(): boolean {
    if (!this.cachedToken) return true;
    return Date.now() + TokenManager.BUFFER_TIME >= this.cachedToken.expiresAt;
  }

  async getToken(forceRefresh = false): Promise<string | null> {
    try {
      // First check our backend JWT in localStorage
      const backendToken = localStorage.getItem('token');
      if (backendToken && !forceRefresh) {
        return backendToken;
      }

      // If no backend token, get Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return null;

      // Return cached token if it's still valid and no force refresh
      if (!forceRefresh && this.cachedToken && !this.isTokenExpired()) {
        return this.cachedToken.token;
      }

      const token = session.access_token;
      
      // Parse token to get expiry
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      const expiresAt = tokenPayload.exp * 1000; // Convert to milliseconds

      // Cache the token
      this.cachedToken = {
        token,
        expiresAt
      };

      return token;
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  }

  clearCache() {
    this.cachedToken = null;
    localStorage.removeItem('token'); // Also clear backend JWT
  }
}

export const tokenManager = new TokenManager(); 